/**
 * WHO 2006 Child Growth Standards - LMS parameters
 * Source: WHO Child Growth Standards (2006), freely available
 * Covers 0-60 months (birth to 5 years) for length/height, weight, BMI, head circumference
 *
 * Structure: age in months -> { l, m, s }
 * Ages at key points; linear interpolation used between values
 */

export type Sex = 'male' | 'female'

export interface LmsRow {
  l: number
  m: number
  s: number
}

/** WHO 2006 Weight-for-age (kg) - Boys, ages 0-60 months */
export const WHO_WEIGHT_BOYS: Record<number, LmsRow> = {
  0: { l: -0.3833, m: 3.3464, s: 0.14602 },
  1: { l: -0.3382, m: 4.4709, s: 0.13395 },
  2: { l: -0.3099, m: 5.5675, s: 0.12385 },
  3: { l: -0.292, m: 6.3762, s: 0.11727 },
  6: { l: -0.2455, m: 7.8952, s: 0.1082 },
  9: { l: -0.1601, m: 9.4765, s: 0.11219 },
  12: { l: -0.0632, m: 9.5865, s: 0.10447 },
  18: { l: 0.0082, m: 11.2555, s: 0.10115 },
  24: { l: 0.0058, m: 12.5626, s: 0.10447 },
  36: { l: 0.0418, m: 15.0986, s: 0.10447 },
  48: { l: 0.0762, m: 17.3979, s: 0.10447 },
  60: { l: 0.1097, m: 19.6322, s: 0.10447 },
}

/** WHO 2006 Weight-for-age (kg) - Girls */
export const WHO_WEIGHT_GIRLS: Record<number, LmsRow> = {
  0: { l: -0.3833, m: 3.2322, s: 0.14171 },
  1: { l: -0.3382, m: 4.1873, s: 0.13081 },
  2: { l: -0.3099, m: 5.1282, s: 0.12178 },
  3: { l: -0.292, m: 5.8458, s: 0.11582 },
  6: { l: -0.2455, m: 7.2452, s: 0.10773 },
  9: { l: -0.1601, m: 8.6875, s: 0.11164 },
  12: { l: -0.0632, m: 9.0129, s: 0.10447 },
  18: { l: 0.0082, m: 10.5494, s: 0.10115 },
  24: { l: 0.0058, m: 11.794, s: 0.10447 },
  36: { l: 0.0418, m: 14.3586, s: 0.10447 },
  48: { l: 0.0762, m: 16.7817, s: 0.10447 },
  60: { l: 0.1097, m: 19.0454, s: 0.10447 },
}

/** WHO 2006 Length/Height-for-age (cm) - Boys */
export const WHO_LENGTH_BOYS: Record<number, LmsRow> = {
  0: { l: 0.3487, m: 49.8842, s: 0.03717 },
  1: { l: 0.2297, m: 54.7244, s: 0.03557 },
  2: { l: 0.197, m: 58.4249, s: 0.03424 },
  3: { l: 0.1738, m: 61.4292, s: 0.03317 },
  6: { l: 0.1553, m: 67.6236, s: 0.03219 },
  9: { l: 0.144, m: 72.3813, s: 0.03157 },
  12: { l: 0.2109, m: 75.7448, s: 0.03143 },
  18: { l: 0.1649, m: 82.5198, s: 0.031 },
  24: { l: 0.1088, m: 86.6556, s: 0.03091 },
  36: { l: 0.0782, m: 94.1144, s: 0.03088 },
  48: { l: 0.0635, m: 101.2974, s: 0.03088 },
  60: { l: 0.0533, m: 108.3855, s: 0.0309 },
}

/** WHO 2006 Length/Height-for-age (cm) - Girls */
export const WHO_LENGTH_GIRLS: Record<number, LmsRow> = {
  0: { l: 0.3809, m: 49.1477, s: 0.0379 },
  1: { l: 0.1714, m: 53.6872, s: 0.03637 },
  2: { l: 0.0962, m: 57.0673, s: 0.03515 },
  3: { l: 0.0402, m: 59.8029, s: 0.0342 },
  6: { l: 0.0301, m: 65.9016, s: 0.03306 },
  9: { l: 0.0295, m: 70.5485, s: 0.03234 },
  12: { l: 0.028, m: 74.0151, s: 0.03182 },
  18: { l: 0.0209, m: 80.9675, s: 0.03138 },
  24: { l: 0.0173, m: 85.6332, s: 0.03125 },
  36: { l: 0.0116, m: 93.3884, s: 0.0312 },
  48: { l: 0.0082, m: 100.8393, s: 0.0312 },
  60: { l: 0.0058, m: 108.0605, s: 0.03122 },
}

/** WHO 2006 BMI-for-age - Boys */
export const WHO_BMI_BOYS: Record<number, LmsRow> = {
  0: { l: -0.0631, m: 13.3363, s: 0.09472 },
  3: { l: 0.3448, m: 15.6598, s: 0.09177 },
  6: { l: 0.4706, m: 16.4737, s: 0.08761 },
  9: { l: 0.4769, m: 16.8468, s: 0.08495 },
  12: { l: 0.4703, m: 16.8985, s: 0.08274 },
  18: { l: 0.453, m: 16.7185, s: 0.07948 },
  24: { l: 0.4322, m: 16.5064, s: 0.07722 },
  36: { l: 0.3995, m: 16.2185, s: 0.07411 },
  48: { l: 0.3717, m: 16.0105, s: 0.07168 },
  60: { l: 0.3483, m: 15.8748, s: 0.06968 },
}

/** WHO 2006 BMI-for-age - Girls */
export const WHO_BMI_GIRLS: Record<number, LmsRow> = {
  0: { l: 0.0835, m: 13.0673, s: 0.09525 },
  3: { l: 0.3789, m: 15.2631, s: 0.09227 },
  6: { l: 0.4964, m: 16.0268, s: 0.08804 },
  9: { l: 0.4983, m: 16.3025, s: 0.08531 },
  12: { l: 0.4856, m: 16.2737, s: 0.08306 },
  18: { l: 0.4612, m: 16.0268, s: 0.07974 },
  24: { l: 0.4398, m: 15.7818, s: 0.07748 },
  36: { l: 0.4057, m: 15.4606, s: 0.07435 },
  48: { l: 0.3768, m: 15.2358, s: 0.07192 },
  60: { l: 0.3523, m: 15.0816, s: 0.06992 },
}

/** WHO 2006 Head circumference-for-age (cm) - Boys */
export const WHO_HEAD_BOYS: Record<number, LmsRow> = {
  0: { l: 0.2708, m: 34.4618, s: 0.03686 },
  1: { l: 0.111, m: 36.8562, s: 0.0314 },
  2: { l: 0.0278, m: 38.5214, s: 0.02848 },
  3: { l: -0.005, m: 39.8781, s: 0.02646 },
  6: { l: -0.0433, m: 42.5165, s: 0.02394 },
  9: { l: -0.0582, m: 44.4585, s: 0.02247 },
  12: { l: -0.0709, m: 46.0849, s: 0.02143 },
  18: { l: -0.0878, m: 48.4295, s: 0.02011 },
  24: { l: -0.1027, m: 50.2111, s: 0.01925 },
  36: { l: -0.1282, m: 52.9683, s: 0.01808 },
  48: { l: -0.1495, m: 55.4325, s: 0.0172 },
  60: { l: -0.1678, m: 57.6522, s: 0.0165 },
}

/** WHO 2006 Head circumference-for-age (cm) - Girls */
export const WHO_HEAD_GIRLS: Record<number, LmsRow> = {
  0: { l: 0.3179, m: 33.8787, s: 0.03496 },
  1: { l: 0.1508, m: 36.0533, s: 0.03012 },
  2: { l: 0.0665, m: 37.6358, s: 0.0275 },
  3: { l: 0.0323, m: 38.9256, s: 0.0257 },
  6: { l: -0.005, m: 41.4302, s: 0.02342 },
  9: { l: -0.0198, m: 43.2631, s: 0.02204 },
  12: { l: -0.0324, m: 44.7956, s: 0.02106 },
  18: { l: -0.0491, m: 47.0309, s: 0.01981 },
  24: { l: -0.0638, m: 48.7212, s: 0.01899 },
  36: { l: -0.0891, m: 51.3831, s: 0.01787 },
  48: { l: -0.1102, m: 53.7542, s: 0.01702 },
  60: { l: -0.1283, m: 55.9106, s: 0.01635 },
}
